import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type ApiResponseError, fetchApiWithToken } from '~/api/utils'
import {
  type OptimisticEventContext,
  useAddOptimisticHistoryEvents,
  useRollbackOptimisticHistoryEvents,
  useUpdateOptimisticEventsWithRealIds
} from '~/domains/actions/hooks/optimisticHistoryUpdates'
import type { Action } from '~/domains/actions/types'
import { generateTaskEvent } from '~/domains/actions/utils/historyEventGenerator'
import { toISOLocal } from '~/utils/date'

type CreateTaskPayload = {
  subject?: string
  scheduledDate?: Date | null
  notes?: string
  assignedUserId?: number
  applicantIds?: number[]
  contactIds?: number[]
  actionTypeId?: number
  actionTypeName?: string
  isCompleted?: boolean
  /** With `isCompleted`; the server defaults to now when omitted. */
  completionDate?: Date
}

export const useCreateTaskMutation = () => {
  const queryClient = useQueryClient()
  const addOptimisticHistoryEvents = useAddOptimisticHistoryEvents()
  const rollbackOptimisticHistoryEvents = useRollbackOptimisticHistoryEvents()
  const updateOptimisticEventsWithRealIds =
    useUpdateOptimisticEventsWithRealIds()

  return useMutation<
    { actions: Action[] },
    ApiResponseError,
    CreateTaskPayload,
    { optimisticEvents: OptimisticEventContext[] }
  >({
    mutationFn: (payload) =>
      fetchApiWithToken({
        method: 'post',
        url: '/v1/actions/tasks',
        data: {
          subject: payload.subject,
          scheduledDate: payload.scheduledDate
            ? toISOLocal(payload.scheduledDate)
            : undefined,
          notes: payload.notes,
          assignedUser: payload.assignedUserId
            ? `/v1/users/${payload.assignedUserId}`
            : undefined,
          applicants: (payload.applicantIds ?? []).map(
            (id) => `/v1/applicants/${id}`
          ),
          contacts: (payload.contactIds ?? []).map(
            (id) => `/v1/contacts/${id}`
          ),
          actionType: payload.actionTypeId
            ? `/v1/taxonomies/action_types/${payload.actionTypeId}`
            : undefined,
          isCompleted: payload.isCompleted,
          completionDate: payload.completionDate
            ? toISOLocal(payload.completionDate)
            : undefined
        }
      }),
    onMutate: (payload) => ({
      optimisticEvents: addOptimisticHistoryEvents(
        payload.applicantIds ?? [],
        (applicantIds) =>
          generateTaskEvent(applicantIds, {
            subject: payload.subject ?? '',
            scheduledDate: payload.scheduledDate,
            notes: payload.notes,
            isCompleted: payload.isCompleted,
            actionType: payload.actionTypeName,
            assignedUser: payload.assignedUserId
              ? `/v1/users/${payload.assignedUserId}`
              : undefined
          })
      )
    }),
    onSuccess: (response, _payload, context) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['actions'] })
      if (context?.optimisticEvents) {
        updateOptimisticEventsWithRealIds(
          context.optimisticEvents,
          response.actions.map((task) => task.id)
        )
      }
    },
    onError: (_error, _payload, context) => {
      if (context?.optimisticEvents) {
        rollbackOptimisticHistoryEvents(context.optimisticEvents)
      }
    }
  })
}
